import { http } from "./http";

/**
 * Chat de desenvolvimento e painéis auxiliares do Builder.
 * O streaming usa SSE (EventSource) com o cookie de sessão do Sanctum.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? "/api";

export type ChatMessageType = "user" | "assistant" | "system" | "error";

export interface RunSummary {
  files_created?: number;
  files_modified?: number;
  migrations_created?: number;
  validation_ok?: boolean;
  tests_passed?: boolean;
  build?: string;
  preview?: string;
  attempts?: number;
  failed_stage?: string | null;
}

export interface ChatMessage {
  uuid: string;
  type: ChatMessageType;
  content: string;
  metadata?: {
    run?: string | null;
    summary?: RunSummary;
    version?: number | null;
    commit?: string | null;
    status?: string;
  } | null;
  created_at?: string | null;
}

export interface Conversation {
  uuid: string;
  title: string | null;
  messages: ChatMessage[];
}

export interface RunStep {
  uuid: string;
  sequence: number;
  type: string;
  status: "running" | "succeeded" | "skipped" | "failed";
  title: string;
  label: string;
}

export interface RunChanges {
  run: string;
  summary: RunSummary;
  version: number | null;
  version_uuid: string | null;
  commit: string | null;
  diff: string;
}

export interface WorkspaceFile {
  path: string;
  type: "file" | "directory";
  size?: number;
}

export interface ProjectVersionItem {
  uuid: string;
  number: number;
  description: string;
  source: string;
  git_commit: string | null;
  author: string | null;
  created_at: string | null;
}

/** Metadados do elemento escolhido no preview (edição visual). */
export interface VisualSelection {
  tag: string;
  text: string;
  classes: string;
  file: string | null;
  component: string | null;
}

export interface StreamHandlers {
  onMessage: (message: ChatMessage) => void;
  onRun: (runUuid: string) => void;
  onStep: (step: RunStep) => void;
  onDone: (message: ChatMessage) => void;
  onError: (message: ChatMessage) => void;
}

export const builderService = {
  conversation: (project: string, conversation?: string) =>
    http.get<Conversation>(
      `/projects/${project}/chat${conversation ? `?conversation=${conversation}` : ""}`,
    ),
  newConversation: (project: string) => http.post<Conversation>(`/projects/${project}/chat`),
  cancel: (project: string, run: string) =>
    http.post<{ uuid: string; status: string }>(`/projects/${project}/chat/runs/${run}/cancel`),
  changes: (project: string, run: string) =>
    http.get<RunChanges>(`/projects/${project}/chat/runs/${run}/changes`),
  retry: (project: string, run: string) =>
    http.post<{ run: string | null; status: string; summary: RunSummary; content: string }>(
      `/projects/${project}/chat/runs/${run}/retry`,
    ),
  files: (project: string) => http.get<WorkspaceFile[]>(`/projects/${project}/files`),
  versions: (project: string) => http.get<ProjectVersionItem[]>(`/projects/${project}/versions`),
  restore: (project: string, version: string) =>
    http.post<{ number: number }>(`/projects/${project}/versions/${version}/restore`),
  gitStatus: (project: string) =>
    http.get<{ repository: boolean; head: string | null; changes: unknown[] }>(
      `/projects/${project}/git/status`,
    ),

  /** Abre o stream SSE da execução. Devolve a função que encerra a conexão. */
  stream(
    project: string,
    conversation: string,
    message: string,
    handlers: StreamHandlers,
    selection?: VisualSelection | null,
  ): () => void {
    const query = new URLSearchParams({ message, conversation });

    // Modo "Selecionar elemento": o backend valida e transforma em contexto.
    if (selection) {
      for (const [key, value] of Object.entries(selection)) {
        if (typeof value === "string" && value !== "") query.set(`selection[${key}]`, value);
      }
    }

    const source = new EventSource(`${BASE_URL}/projects/${project}/chat/stream?${query}`, {
      withCredentials: true,
    });

    const parse = <T>(event: MessageEvent): T => JSON.parse(event.data) as T;

    source.addEventListener("message", (event) =>
      handlers.onMessage(parse<ChatMessage>(event as MessageEvent)),
    );
    source.addEventListener("run", (event) =>
      handlers.onRun(parse<{ uuid: string }>(event as MessageEvent).uuid),
    );
    source.addEventListener("step", (event) =>
      handlers.onStep(parse<RunStep>(event as MessageEvent)),
    );
    source.addEventListener("done", (event) => {
      handlers.onDone(parse<ChatMessage>(event as MessageEvent));
      source.close();
    });
    source.addEventListener("error", (event) => {
      const data = (event as MessageEvent).data;

      if (typeof data === "string" && data.length > 0) {
        handlers.onError(JSON.parse(data) as ChatMessage);
      } else {
        handlers.onError({
          uuid: `local-${Date.now()}`,
          type: "error",
          content: "A conexão com o servidor foi interrompida.",
        });
      }

      source.close();
    });

    return () => source.close();
  },
};
